/**
 * Batch E commit E2 — SupervisorAgentDetailPage.
 *
 * Drill-down detail route for a single agent on the mobile flow.
 * Reads the agent identified by the `:agentId` route param from the
 * supervisor store and exposes the same Force Pause / Force Logout
 * actions that AgentGrid renders on desktop — both emit through the
 * `supervisor:*` socket events that the worker consumer carries out.
 */
import type { JSX } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'
import type { AgentStatus } from '@kc3/types'
import { getSocket } from '../../socket/client.js'
import { useSupervisorStore } from '../../store/supervisorStore.js'

type Severity = 'success' | 'warning' | 'danger' | 'info' | 'secondary'

const STATUS_SEVERITY: Record<AgentStatus, Severity> = {
  IDLE: 'success',
  AVAILABLE_ON_DEMAND: 'success',
  IN_CALL: 'info',
  RINGING: 'warning',
  PAUSED: 'warning',
  ON_BREAK: 'secondary',
  ACW: 'warning',
  OFFLINE: 'danger',
  // Reservation FSM states; map is exhaustive over AgentStatus.
  REGISTERED: 'secondary',
  RESERVING: 'warning',
  RESERVED: 'warning',
  INVITING: 'warning',
  ANSWERED: 'info',
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = String(sec % 60).padStart(2, '0')
  return `${m}:${s}`
}

export default function SupervisorAgentDetailPage(): JSX.Element {
  const { agentId } = useParams<{ agentId: string }>()
  const navigate = useNavigate()
  const agent = useSupervisorStore((s) =>
    agentId ? s.agentList.find((a) => a.id === agentId) ?? null : null,
  )

  function forcePause(): void {
    if (agentId) getSocket().emit('supervisor:force_pause', { agentId })
  }
  function forceLogout(): void {
    if (agentId) getSocket().emit('supervisor:force_logout', { agentId })
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-2">
      <div className="flex items-center gap-2">
        <Button
          icon="pi pi-arrow-left"
          text
          aria-label="Back to supervisor"
          onClick={() => navigate('/supervisor')}
        />
        <h1 className="text-sm font-semibold text-gray-800">Agent detail</h1>
      </div>

      {!agent ? (
        <div className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-500">
          Agent not found. They may have logged out — return to the supervisor
          dashboard for an up-to-date list.
        </div>
      ) : (
        <section className="rounded border border-gray-200 bg-white p-4 shadow-sm">
          <header className="mb-3 flex items-center gap-3">
            <span className="text-base font-semibold">{agent.name}</span>
            <Tag
              value={agent.status}
              severity={STATUS_SEVERITY[agent.status] ?? 'secondary'}
            />
          </header>
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-gray-500">Calls handled</dt>
            <dd>{agent.callsHandled}</dd>
            {agent.status === 'IN_CALL' && (
              <>
                <dt className="text-gray-500">Talk time</dt>
                <dd>{formatDuration(agent.callDurationSec)}</dd>
              </>
            )}
            {agent.currentQueueId && (
              <>
                <dt className="text-gray-500">Queue</dt>
                <dd className="font-mono text-xs">{agent.currentQueueId}</dd>
              </>
            )}
          </dl>
          <div className="mt-4 flex gap-2">
            <Button
              icon="pi pi-pause"
              label="Force Pause"
              severity="warning"
              onClick={forcePause}
            />
            <Button
              icon="pi pi-sign-out"
              label="Force Logout"
              severity="danger"
              onClick={forceLogout}
            />
          </div>
        </section>
      )}
    </div>
  )
}
